import { useEffect, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Page from "../Components/Page";
import { axiosInstance } from "../Components/axiosInstance.js";
import { useAuth } from "../Components/Auth/useAuth";
import {
  GrowerSurface,
  WorkspaceState,
} from "../Components/GrowerLayout.jsx";

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50];

function normalizePlatformUserResponse(payload, fallbackPage, fallbackPageSize) {
  const response = payload?.data && !Array.isArray(payload.data) ? payload.data : payload;
  const items = Array.isArray(response?.items) ? response.items : [];

  return {
    items,
    total: Number(response?.total) || items.length,
    page: Number(response?.page) || fallbackPage,
    pageSize: Number(response?.page_size) || fallbackPageSize,
  };
}

function formatName(user) {
  return [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() || "Unknown";
}

function formatDate(value) {
  if (!value) {
    return "Never";
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString();
}

function getPortalForAccountType(accountType) {
  switch (String(accountType || "").toUpperCase()) {
    case "PARTNER":
      return "partner";
    case "AGGREGATOR":
      return "aggregator";
    case "ADMIN":
      return "admin";
    case "GROWER":
    default:
      return "grower";
  }
}

export default function AdminImpersonationPage() {
  const { user, isImpersonating, impersonatedEmail, startImpersonation } = useAuth();
  const [draftFilters, setDraftFilters] = useState({
    email: "",
    name: "",
  });
  const [filters, setFilters] = useState({
    email: "",
    name: "",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let isMounted = true;

    async function loadUsers() {
      setIsLoading(true);
      setErrorMessage("");

      try {
        const response = await axiosInstance.get("/admin/platform-users", {
          params: {
            page,
            page_size: pageSize,
            ...(filters.email ? { email: filters.email } : {}),
            ...(filters.name ? { name: filters.name } : {}),
          },
        });

        if (!isMounted) {
          return;
        }

        const normalized = normalizePlatformUserResponse(response.data, page, pageSize);
        setUsers(normalized.items);
        setTotal(normalized.total);
        setPage(normalized.page);
        setPageSize(normalized.pageSize);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setUsers([]);
        setTotal(0);
        setErrorMessage("We couldn't load platform users right now.");
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadUsers();

    return () => {
      isMounted = false;
    };
  }, [filters, page, pageSize]);

  const totalPages = useMemo(() => {
    if (!total) {
      return 1;
    }

    return Math.max(1, Math.ceil(total / pageSize));
  }, [pageSize, total]);

  const normalizedSignedInEmail = (user?.email || "").trim().toLowerCase();
  const normalizedImpersonatedEmail = (impersonatedEmail || "").trim().toLowerCase();

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setFilters({
      email: draftFilters.email.trim(),
      name: draftFilters.name.trim(),
    });
  };

  const handleClearFilters = () => {
    setDraftFilters({
      email: "",
      name: "",
    });
    setPage(1);
    setFilters({
      email: "",
      name: "",
    });
  };

  const handleImpersonationStart = (platformUser) => {
    startImpersonation(platformUser.email, getPortalForAccountType(platformUser.account_type));
  };

  return (
    <Page
      title="User impersonation"
      headerBorderColor="border-[#f15d22]"
      contentWidth="wide"
      introContent="Search platform users and open their live portal context for support and troubleshooting."
      padding="px-4 py-5 md:px-6 md:py-7"
    >
      <div className="flex h-full w-full flex-col gap-5 text-slate-600">
        <GrowerSurface className="flex flex-col gap-4" padding="p-5 md:p-6">
          <form className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto] gap-4 items-end" onSubmit={handleSearchSubmit}>
            <TextField
              label="Filter by email"
              value={draftFilters.email}
              onChange={(event) =>
                setDraftFilters((current) => ({
                  ...current,
                  email: event.target.value,
                }))
              }
              size="small"
            />
            <TextField
              label="Filter by name"
              value={draftFilters.name}
              onChange={(event) =>
                setDraftFilters((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
              size="small"
            />
            <Button type="submit" variant="contained" sx={{ height: 40 }}>
              Search
            </Button>
            <Button type="button" variant="outlined" sx={{ height: 40 }} onClick={handleClearFilters}>
              Clear
            </Button>
          </form>

          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-[#555]">
              {isImpersonating
                ? `Currently impersonating ${impersonatedEmail}.`
                : "No user impersonation session is active."}
            </div>

            <TextField
              select={true}
              SelectProps={{ native: true }}
              label="Rows per page"
              size="small"
              value={pageSize}
              onChange={(event) => {
                const nextPageSize = Number(event.target.value) || DEFAULT_PAGE_SIZE;
                setPage(1);
                setPageSize(nextPageSize);
              }}
              sx={{ minWidth: 140 }}
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </TextField>
          </div>
        </GrowerSurface>

        <GrowerSurface className="overflow-hidden" padding="p-0">
          {isLoading ? (
            <WorkspaceState state="loading" title="Loading platform users" />
          ) : errorMessage ? (
            <WorkspaceState state="error" title={errorMessage} />
          ) : users.length === 0 ? (
            <WorkspaceState
              title="No users matched the current filters"
              description="Clear one or both filters to broaden the results."
            />
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] border-collapse">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-sm text-slate-700">
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Account Type</th>
                  <th className="px-4 py-3 font-semibold">Created</th>
                  <th className="px-4 py-3 font-semibold">Last Sign In</th>
                  <th className="px-4 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((platformUser) => {
                  const normalizedUserEmail = (platformUser.email || "").trim().toLowerCase();
                  const isSignedInAdmin = normalizedUserEmail === normalizedSignedInEmail;
                  const isActiveImpersonation =
                    normalizedUserEmail && normalizedUserEmail === normalizedImpersonatedEmail;
                  const actionDisabled =
                    !normalizedUserEmail ||
                    isSignedInAdmin ||
                    isActiveImpersonation ||
                    isImpersonating;

                  return (
                    <tr
                      key={platformUser.public_id || platformUser.id}
                      className="border-b border-slate-200 align-top last:border-b-0 hover:bg-slate-50"
                    >
                      <td className="px-4 py-3">{formatName(platformUser)}</td>
                      <td className="px-4 py-3">{platformUser.email || "Unknown"}</td>
                      <td className="px-4 py-3">{platformUser.account_type || "Unknown"}</td>
                      <td className="px-4 py-3">{formatDate(platformUser.created_at)}</td>
                      <td className="px-4 py-3">{formatDate(platformUser.last_sign_in)}</td>
                      <td className="px-4 py-3">
                        <Button
                          variant={isActiveImpersonation ? "outlined" : "contained"}
                          disabled={actionDisabled}
                          onClick={() => handleImpersonationStart(platformUser)}
                        >
                          {isSignedInAdmin
                            ? "Signed-in admin"
                            : isActiveImpersonation
                              ? "Impersonating"
                              : isImpersonating
                                ? "Session active"
                                : "Impersonate"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </GrowerSurface>

        <GrowerSurface className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between" padding="p-4">
          <div className="text-sm text-[#555]">
            Showing page {page} of {totalPages} ({total} total users)
          </div>

          <div className="flex gap-3">
            <Button
              variant="outlined"
              disabled={page <= 1 || isLoading}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              disabled={page >= totalPages || isLoading}
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            >
              Next
            </Button>
          </div>
        </GrowerSurface>
      </div>
    </Page>
  );
}
